import { useCallback, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "./reduxHooks";
import useTrayPrinter from "./useTrayPrinter";
import { useTranslation } from "../util/translation-provider";
import {
  buildTicketPrintRequest,
  loadPrintableByInvoice,
  PrintablePayload,
} from "../util/ticket-print";
import {
  PrintCopyChoice,
  PrintFeedback,
} from "../components/pos/payment-modal/PrintCopyButtons";

// Print choices for bills that are already saved, loading each bill back from
// the server the same way Reprint does. Used where a transaction ends without
// a print snapshot of its own - a partial payment, a payment-only pickup, an
// edited bill - so every one of them still ends on the print buttons.
const useSavedBillPrinter = () => {
  const dispatch = useAppDispatch();
  const translation = useTranslation();
  const { user } = useAppSelector((state) => state.auth);
  const { printTicket } = useTrayPrinter();

  const [billIds, setBillIds] = useState<number[]>([]);
  const [printingCopy, setPrintingCopy] = useState<PrintCopyChoice | null>(null);
  const [printFeedback, setPrintFeedback] = useState<PrintFeedback | null>(null);
  // Loaded once per prompt, so printing the other copy afterwards (or
  // retrying a failed one) doesn't refetch every bill.
  const payloadsRef = useRef(new Map<number, PrintablePayload>());

  const prepare = useCallback((ids: number[]) => {
    payloadsRef.current = new Map();
    setBillIds(ids);
    setPrintingCopy(null);
    setPrintFeedback(null);
  }, []);

  const handlePrintSelect = (copy: PrintCopyChoice) => {
    // "none" fires when the popup closes (Done / X).
    if (copy === "none") {
      prepare([]);
      return;
    }
    if (printingCopy) return;
    if (billIds.length === 0) {
      setPrintFeedback({ copy, success: false, message: "Nothing to print for this receipt." });
      return;
    }

    setPrintingCopy(copy);
    setPrintFeedback(null);

    (async () => {
      const copies: ("storeCopy" | "receipt")[] =
        copy === "both" ? ["storeCopy", "receipt"] : [copy];
      const cashier = {
        name: `${user?.firstName ?? ""} ${user?.lastName ?? ""}`.trim(),
        id: user?.id,
      };
      let attempts = 0;
      let failures = 0;
      let loadFailed = false;

      for (const id of billIds) {
        let payload = payloadsRef.current.get(id);
        if (!payload) {
          payload = (await loadPrintableByInvoice(dispatch, translation, String(id))) ?? undefined;
          if (payload) payloadsRef.current.set(id, payload);
        }
        for (const copyType of copies) {
          attempts++;
          if (!payload) {
            failures++;
            loadFailed = true;
            continue;
          }
          const ok = await printTicket(buildTicketPrintRequest(payload, copyType, cashier));
          if (!ok) failures++;
        }
      }

      setPrintingCopy(null);
      setPrintFeedback({
        copy,
        success: failures === 0,
        message:
          failures === 0
            ? undefined
            : loadFailed
              ? "Could not load the bill for printing."
              : failures === attempts
                ? "Could not reach the printer. Make sure Presto Tray is running on this computer."
                : `${failures} of ${attempts} copies failed to print.`,
      });
    })();
  };

  return { billIds, prepare, handlePrintSelect, printingCopy, printFeedback };
};

export default useSavedBillPrinter;
